/**
 * Every word and number the hangar screen shows, as pure functions, so the display rules are tested
 * rather than eyeballed:
 *
 * - **`null` is "not measured", shown as "—"**, never as 0, calm or clear.
 * - **A limit with no reading yet says "no reading yet"**, never "within": silence is not calm.
 * - **A limit on a silent source says so**: "past when last heard from" is shown with the time the
 *   source went quiet, not as a live "past".
 * - **Limits are the owner's own numbers**, quoted the way the owner typed them ("your 20 mph").
 */

import {
  describeAge,
  formatAltimeter,
  formatTemperature,
  formatWind,
  type Observation,
  type WindUnit,
} from '@skyweather/weather-core'
import type { LimitMetric, LimitStatus, SiteUnits, SourceHealth, SourceReading } from './types.js'

export const windUnit = (u: SiteUnits): WindUnit => u.wind ?? 'kt'

/** "14:22" in the site's own timezone. */
export function localTime(t: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(t)
}

/**
 * A time with its day when it is not today, site-local: "14:05", "yesterday 23:49", "Sep 30, 23:49".
 * A bare "23:49" for last night would read as a few minutes ago on a screen glanced at in the afternoon.
 */
export function localWhen(t: number, now: number, timeZone: string): string {
  const day = (x: number) => new Intl.DateTimeFormat('en-CA', { timeZone }).format(x)
  const time = localTime(t, timeZone)
  if (day(t) === day(now)) return time
  if (day(t) === day(now - 86_400_000)) return `yesterday ${time}`
  const date = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric' }).format(t)
  return `${date}, ${time}`
}

/** "Fort Morgan Muni, CO (KFMM)": NOAA's name without the country, then the code. */
export function fieldName(field: { icao: string; name: string | null } | null | undefined): string | null {
  if (!field) return null
  const name = field.name?.replace(/,\s*US$/, '').trim()
  return name ? `${name} (${field.icao})` : field.icao
}

/** "20:22Z". */
export function zuluTime(t: number): string {
  const d = new Date(t)
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}Z`
}

/** The big wind line: "14 kt", "Calm", or "—". */
export function windHeadline(o: Observation | null, u: SiteUnits): string {
  if (!o || o.windKt === null) return '—'
  if (o.windKt === 0 && (o.gustKt === null || o.gustKt === 0)) return 'Calm'
  return formatWind(o.windKt, windUnit(u), { withKnots: false })
}

/** "from 270°", "variable", "from 250°–310°", or "" when calm or not measured. */
export function windDirection(o: Observation | null): string {
  if (!o || o.windKt === null || o.windKt === 0) return ''
  if (o.windVariable) {
    return o.windVarFromDeg !== null && o.windVarToDeg !== null ? `variable ${o.windVarFromDeg}°–${o.windVarToDeg}°` : 'variable'
  }
  if (o.windDirDeg === null) return ''
  const range = o.windVarFromDeg !== null && o.windVarToDeg !== null ? ` (${o.windVarFromDeg}°–${o.windVarToDeg}°)` : ''
  return `from ${String(o.windDirDeg).padStart(3, '0')}°${range}`
}

export function gustSpread(o: Observation | null): number | null {
  if (!o || o.gustKt === null || o.windKt === null) return null
  return Math.max(0, o.gustKt - o.windKt)
}

/** "5,500 ft broken", "No ceiling", or null when the source does not measure the sky. */
export function ceilingText(o: Observation | null): string | null {
  if (!o || o.ceilingFtAgl === null) return null
  if (o.ceilingFtAgl === 'none') return 'No ceiling'
  const layer = o.clouds?.find((c) => c.baseFtAgl === o.ceilingFtAgl && (c.cover === 'BKN' || c.cover === 'OVC' || c.cover === 'VV'))
  const cover = layer ? { BKN: 'broken', OVC: 'overcast', VV: 'sky obscured', FEW: '', SCT: '' }[layer.cover] : ''
  return `${o.ceilingFtAgl.toLocaleString('en-US')} ft${cover ? ` ${cover}` : ''}`
}

export function visibilityText(o: Observation | null, u: SiteUnits): string | null {
  if (!o || o.visibilitySm === null) return null
  if (u.distance === 'km') return `${(o.visibilitySm * 1.609344).toFixed(o.visibilitySm < 3 ? 1 : 0)} km`
  return `${o.visibilitySm < 3 ? o.visibilitySm.toFixed(2).replace(/0$/, '') : Math.round(o.visibilitySm)} mi`
}

/** "Temp / dew 24 °C / 6 °C", or just "Temp 24 °C" from a station with no dew point. */
export function tempDew(o: Observation | null, u: SiteUnits): { label: string; value: string } | null {
  if (!o || (o.tempC === null && o.dewpointC === null)) return null
  const unit = u.temp ?? 'C'
  if (o.dewpointC === null) return { label: 'Temp', value: formatTemperature(o.tempC, unit) }
  return { label: 'Temp / dew', value: `${formatTemperature(o.tempC, unit)} / ${formatTemperature(o.dewpointC, unit)}` }
}

/** Airport reports and AWOS give an altimeter setting; home stations a sea-level barometer. */
export function pressureText(r: SourceReading, u: SiteUnits): { label: string; value: string } | null {
  const o = r.observation
  if (!o || o.altimeterInHg === null) return null
  const label = r.descriptor.kind === 'noaa-metar' || r.descriptor.kind === 'awos-bridge' ? 'Altimeter' : 'Barometer'
  return { label, value: formatAltimeter(o.altimeterInHg, u.pressure ?? 'inHg') }
}

/** "observed 20 s ago" for a fresh minute summary, otherwise weather-core's wording. */
export function ageText(observedAt: number | null, now: number): string {
  if (observedAt === null) return 'never reported'
  const s = Math.max(0, Math.round((now - observedAt) / 1000))
  return s < 60 ? `${s} s ago` : describeAge(s)
}

export function healthBadge(health: SourceHealth, since: number, now: number): string | null {
  if (health === 'reporting') return null
  const age = describeAge(Math.max(0, (now - since) / 1000)).replace(/ ago$/, '')
  return health === 'late' ? `Late · ${age}` : `Not reporting · ${age}`
}

const METRIC_NAME: Record<LimitMetric, string> = {
  windKt: 'Wind',
  gustKt: 'Gust',
  gustSpreadKt: 'Gust spread',
  ceilingFtAgl: 'Ceiling',
  visibilitySm: 'Visibility',
  tempC: 'Temperature',
  precipitation: 'Precipitation',
}

/** A reading in the metric's own terms, in the site's units where it has one. */
export function metricValue(metric: LimitMetric, v: LimitStatus['lastValue'], u: SiteUnits): string {
  if (v === null) return '—'
  if (metric === 'precipitation') return v === true ? 'yes' : 'none'
  if (v === 'none') return 'no ceiling'
  if (typeof v !== 'number') return '—'
  switch (metric) {
    case 'windKt':
    case 'gustKt':
    case 'gustSpreadKt':
      return formatWind(v, windUnit(u), { withKnots: false, role: metric === 'gustKt' ? 'gust' : 'average' })
    case 'ceilingFtAgl':
      return `${Math.round(v).toLocaleString('en-US')} ft`
    case 'visibilitySm':
      return `${v} mi`
    case 'tempC':
      return formatTemperature(v, u.temp ?? 'C')
  }
}

/** "your 20 kt": the number exactly as the owner typed it. */
export function ownerLimit(s: LimitStatus): string {
  const r = s.rule
  if (r.metric === 'precipitation') return 'your no-precipitation rule'
  const value = r.enteredValue ?? r.limit
  const unit = r.enteredUnit ?? r.unit ?? ''
  const shown = value === null ? '—' : value.toLocaleString('en-US')
  return `your ${shown}${unit ? ` ${unit === 'C' || unit === 'F' ? `°${unit}` : unit}` : ''}`
}

export interface LimitChip {
  setName: string
  text: string
  tone: 'past' | 'approaching' | 'within' | 'unknown' | 'stale'
}

export function limitChip(s: LimitStatus, u: SiteUnits, timeZone: string, now: number = Date.now()): LimitChip {
  const what = `${METRIC_NAME[s.rule.metric]} ${metricValue(s.rule.metric, s.lastValue, u)}`
  if (s.state === 'unknown') return { setName: s.rule.setName, text: 'no reading yet', tone: 'unknown' }
  if (s.sourceHealth === 'not_reporting') {
    const at = s.lastEvidenceAt !== null ? ` at ${localWhen(s.lastEvidenceAt, now, timeZone)}` : ''
    return {
      setName: s.rule.setName,
      text: `${s.state} when last heard from${at} · no new reading`,
      tone: 'stale',
    }
  }
  if (s.state === 'past') return { setName: s.rule.setName, text: `${what} · past ${ownerLimit(s)}`, tone: 'past' }
  if (s.state === 'approaching') return { setName: s.rule.setName, text: `${what} · approaching ${ownerLimit(s)}`, tone: 'approaching' }
  return { setName: s.rule.setName, text: `within ${ownerLimit(s)}`, tone: 'within' }
}

/** Order limit chips so the ones that matter are first: past, approaching, stale, unknown, within. */
export function chipOrder(c: LimitChip): number {
  return { past: 0, approaching: 1, stale: 2, unknown: 3, within: 4 }[c.tone]
}

/** Points for a sparkline: wind and gust over the trend window, scaled into a box. */
export function sparkline(points: Observation[], from: number, to: number, width: number, height: number) {
  const usable = points.filter((p) => p.observedAt >= from && p.observedAt <= to && p.windKt !== null)
  const max = Math.max(10, ...usable.map((p) => p.gustKt ?? p.windKt ?? 0)) * 1.1
  const x = (t: number) => ((t - from) / (to - from)) * width
  const y = (v: number) => height - (v / max) * height
  const line = (pick: (o: Observation) => number | null) =>
    usable
      .map((p) => [p, pick(p)] as const)
      .filter((pair): pair is readonly [Observation, number] => pair[1] !== null)
      .map(([p, v]) => `${x(p.observedAt).toFixed(1)},${y(v).toFixed(1)}`)
      .join(' ')
  return { wind: line((o) => o.windKt), gust: line((o) => o.gustKt), count: usable.length }
}
